// =============================================
// dnsChangeLogService — append-only log akcji DNS (tabela dns_change_log).
//
// insert = BEST-EFFORT: blad zapisu logu NIE moze przerwac runu kreatora
// (NS juz przepisane w AfterMarket — log jest wtorny). Logujemy przez
// contentLogger i jedziemy dalej.
// RLS: is_allowed_user() AND can_see_workspace(workspace) — pracownik bez
// flagi nie widzi wierszy Sponsorakow.
// =============================================
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/services/contentEngine/contentLogger";
import type { AmWorkspace } from "@/services/aftermarketService";

export type DnsChangeAction = "zone_create" | "ns_set" | "a_record";
export type DnsChangeStatus = "success" | "failed" | "skipped";

export interface DnsChangeLogInsert {
  website_id: string | null;
  domain: string;
  action: DnsChangeAction;
  cf_account_id: string | null;
  workspace: AmWorkspace;
  before: unknown | null;
  after: unknown | null;
  status: DnsChangeStatus;
  /** failed: tresc bledu; skipped: powod pominiecia */
  error: string | null;
}

export interface DnsChangeLogRow extends DnsChangeLogInsert {
  id: string;
  user_id: string | null;
  created_at: string;
}

export const DNS_ACTION_LABEL: Record<DnsChangeAction, string> = {
  zone_create: "zona w CF",
  ns_set: "NS w AfterMarket",
  a_record: "rekord A",
};

/** PostgrestError nie jest instancja Error — String(err) dawalo "[object Object]" w logach. */
function errText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === "object" && "message" in err) { const e = err as { message?: string; code?: string; details?: string }; return [e.code, e.message, e.details].filter(Boolean).join(" · "); }
  return String(err);
}

export async function insertDnsChangeLog(row: DnsChangeLogInsert): Promise<void> {
  try {
    const { error } = await (supabase as any).from("dns_change_log").insert({
      website_id: row.website_id,
      domain: row.domain,
      action: row.action,
      cf_account_id: row.cf_account_id,
      workspace: row.workspace,
      before: row.before ?? null,
      after: row.after ?? null,
      status: row.status,
      error: row.error,
    });
    if (error) throw error;
  } catch (err) {
    logError("DnsChangeLog", `Zapis logu nieudany (${row.action} ${row.domain}): ${errText(err)}`);
  }
}

export async function listDnsChangeLog(opts: { limit?: number; domain?: string } = {}): Promise<DnsChangeLogRow[]> {
  const limit = opts.limit ?? 100;
  let q = (supabase as any)
    .from("dns_change_log")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  const d = opts.domain?.trim().toLowerCase();
  if (d) q = q.ilike("domain", `%${d}%`);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data || []) as DnsChangeLogRow[];
}
